import {
  DataTable,
  Spinner,
  Text,
  Button,
  SecureText,
} from 'components/Elements';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useActiveCustomers } from '../api/getActiveCustomers';
import { imageToBlob } from 'utils/ImageToBlob';
import { Authorization, ROLES } from 'lib/authorization';
import { Sessions } from './Sessions';

export const ActiveCustomers = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [starredUsers, setStarredUsers] = useState(() =>
    JSON.parse(localStorage.getItem('starredUsers') || '{}')
  );

  const activeCustomersQuery = useActiveCustomers({ pageNumber });

  useEffect(() => {
    localStorage.setItem('starredUsers', JSON.stringify(starredUsers));
  }, [starredUsers]);

  if (activeCustomersQuery.isLoading) {
    return (
      <div className="w-full h-48 flex justify-center items-center">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!activeCustomersQuery.data) return null;

  const { activeUsers, totalPages, activeUsersTotal } =
    activeCustomersQuery.data;

  const columns = [
    {
      accessorKey: 'firstName',
      header: 'User',
      cell: ({ row }) => {
        const { id, firstName, lastName, username, middlename, photo } =
          row.original;
        return (
          <Link
            to={`/app/users/${id}`}
            className="flex flex-row items-center space-x-2 justify-start"
          >
            {photo ? (
              <CustomerAvatar photo={photo} />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-white font-bold">
                  {firstName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex flex-col">
              <Text variant="body">
                {firstName} {middlename} {lastName}
              </Text>
              <Text className="text-xs" variant="body">
                {username || <em>No Username</em>}
              </Text>
            </div>
          </Link>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <>
          <SecureText
            value={row.original.email}
            allowedRoles={[ROLES.ROOT_USER]}
            // requiredPermissions={['viewCustomerProfiles']}
            className="text-sm"
          />
          <br />
          <SecureText
            value={row.original.phoneNumber}
            allowedRoles={[ROLES.ROOT_USER]}
            // requiredPermissions={['viewCustomerProfiles']}
            className="text-sm"
          />
        </>
      ),
    },
    {
      accessorKey: 'wallets',
      header: 'Vaults',
      cell: ({ row }) => {
        const wallets = row.original.wallets || [];
        const visibleWallets = wallets.slice(0, 3);
        const remaining = wallets.length - visibleWallets.length;

        if (!wallets.length) {
          return <span className="text-white/50 text-xs">No vaults</span>;
        }

        return (
          <div className="flex -space-x-4 rtl:space-x-reverse justify-start">
            {visibleWallets.map((wallet, index) => (
              <img
                key={wallet.id}
                className="w-7 h-7 border-2 border-white rounded-full dark:border-white/10 shadow-sm"
                src={`https://flagcdn.com/h20/${wallet.currency.flagCode.toLowerCase()}.png`}
                alt={wallet.currency.currencyCode}
                title={wallet.currency.currencyCode}
              />
            ))}

            {remaining > 0 && (
              <div className="flex items-center justify-center w-7 h-7 text-[10px] font-medium text-white bg-white/10 border-2 border-white rounded-full dark:border-white/10 backdrop-blur-sm">
                +{remaining}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="bg-white/10  w-fit rounded-md p-1 ">
          {row.original.status === 1 ? (
            <div className="flex flex-row items-center space-x-1">
              <div className="h-2 w-2 bg-green-700 rounded-full" />
              <span>Active</span>
            </div>
          ) : (
            <div className="flex flex-row items-center space-x-1">
              <div className="h-2 w-2 bg-red-700 rounded-full" />
              <span>Inactive</span>
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined date',
      cell: ({ row }) => <Text>{row.original.createdAt || '-'}</Text>,
    },
    {
      accessorKey: 'allow2FA',
      header: '2F Auth',
      cell: ({ row }) => (
        <span className="bg-orange-400/40 rounded-md p-1">Enabled</span>
      ),
    },
    {
      accessorKey: 'country',
      header: 'Country',
      cell: ({ row }) => {
        const wallets = row.original.wallets || [];
        const defaultWallet = wallets[0];

        if (!defaultWallet?.currency) {
          return <span className="text-white/50 text-xs">N/A</span>;
        }

        const { flagCode, country } = defaultWallet.currency;

        return (
          <div className="flex items-center gap-2">
            <img
              src={`https://flagcdn.com/h20/${flagCode.toLowerCase()}.png`}
              alt={country}
              className="w-4 h-4 rounded-full shadow-sm"
            />
            <span className="text-xs text-white/80">{country}</span>
          </div>
        );
      },
    },

    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Authorization
          allowedRoles={[ROLES.ROOT_USER]}
          // requiredPermissions={['manageUsers', 'manageRoles']}
          forbiddenFallback={<div>-</div>}
        >
          <div className="group relative w-fit flex flex-row items-center space-x-1">
            <div className="border border-white/10 p-1 px-2 rounded-lg hover:bg-white/10 text-xs">
              Block
            </div>
            <div className="border border-white/10 p-1 px-2 rounded-lg hover:bg-white/10 text-xs">
              Ban
            </div>
            <Sessions userId={row.original.id} />
          </div>
        </Authorization>
      ),
    },
  ];

  const CustomerAvatar = async (photo) => {
    const localURL = await imageToBlob(photo);
    return <img src={localURL} alt="Avatar" className="w-4 h-4 rounded-full" />;
  };

  return (
    <div className="space-y-4">
      <DataTable
        data={activeUsers}
        columns={columns}
        totalPages={totalPages}
        currentPage={pageNumber}
        onPageChange={setPageNumber}
      />
    </div>
  );
};
